import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CtaButton } from './CtaButton';
import { ExitIntentUpsell } from './ExitIntentUpsell';
import { Reveal } from './Reveal';
import { CheckIcon, DropletIcon, LeafIcon, ShieldHeartIcon, SparkIcon } from './icons/Icons';
type OrderBump = {
  id: string;
  eyebrow: string;
  title: string;
  description: string;
  benefits: string[];
  price: number;
  originalPrice: number;
  image: string;
  alt: string;
  icon: typeof LeafIcon;
};
const orderBumps: OrderBump[] = [{
  id: 'jugos-7-dias',
  eyebrow: 'Acelerador 1 · Protocolo Jugos 7 Días',
  title: 'SÍ, QUIERO SANAR EN DÍAS, NO EN MESES',
  description: 'Activa las fórmulas reconstructoras que ayudan a crear la “venda líquida” desde el primer día. Dejarlo fuera significa soportar el ardor más tiempo del necesario.',
  benefits: ['Jugos reconstructores con sábila, col y alimentos mucilaginosos.', 'Preparaciones rápidas para acelerar la protección de tu mucosa.', 'Una ruta práctica de 7 días para empezar sin improvisar.'],
  price: 9,
  originalPrice: 47,
  image: 'https://cdn.magicpatterns.com/uploads/iQqd3qVnnH2inNuXafeGXq/BUM-1.png',
  alt: 'Mockup del Protocolo de 7 Días, Jugos Reconstructores',
  icon: DropletIcon
}, {
  id: 'supervivencia-restaurantes',
  eyebrow: 'Acelerador 2 · Guía de Supervivencia en Restaurantes',
  title: 'SÍ, QUIERO DEJAR DE ESCONDERME EN LAS REUNIONES',
  description: 'Vuelve a compartir una mesa con tu familia sin miedo a terminar la noche con dolor o reflujo. Dejarla fuera significa seguir prisionera de tu casa.',
  benefits: ['Qué pedir y qué evitar en restaurantes comunes.', 'Cómo proteger tu estómago antes de salir a comer.', 'Decisiones simples para disfrutar sin pasar vergüenza.'],
  price: 9,
  originalPrice: 47,
  image: 'https://cdn.magicpatterns.com/uploads/nPhqbazz5r4B1BaxRge6UM/BUM-2.png',
  alt: 'Mockup de la Guía de Supervivencia en Restaurantes',
  icon: LeafIcon
}, {
  id: 'manual-sos',
  eyebrow: 'Acelerador 3 · Manual SOS Anti-Crisis',
  title: 'SÍ, QUIERO MI “SEGURO DE VIDA” PARA LAS CRISIS',
  description: 'Ten tu extintor natural listo para esas noches en que el dolor aparece sin avisar. Dejarlo fuera significa quedar indefensa cuando más lo necesites.',
  benefits: ['Remedios caseros de efecto inmediato para crisis agudas.', 'La posición física de rescate para detener el reflujo.', 'Respiración guiada para calmar el nervio vago y reducir la tensión.'],
  price: 9,
  originalPrice: 47,
  image: 'https://cdn.magicpatterns.com/uploads/rm7dY19KpkNfZ5FXe6jGLH/BUM-3.png',
  alt: 'Mockup del Manual SOS, El Extintor Natural',
  icon: ShieldHeartIcon
}];
const BASE_PRICE = 10.99;
const BASE_CHECKOUT_URL = 'https://rutinasimple.com//cart/52838156763417:1';
const BUMP_ORDER = ['jugos-7-dias', 'supervivencia-restaurantes', 'manual-sos'];
const CHECKOUT_URLS: Record<string, string> = {
  'jugos-7-dias': 'https://rutinasimple.com//cart/52919637737753:1',
  'supervivencia-restaurantes': 'https://rutinasimple.com//cart/52919667163417:1',
  'manual-sos': 'https://rutinasimple.com//cart/52919675191577:1',
  'jugos-7-dias|supervivencia-restaurantes': 'https://rutinasimple.com//cart/52919692787993:1',
  'jugos-7-dias|manual-sos': 'https://rutinasimple.com//cart/52919700554009:1',
  'supervivencia-restaurantes|manual-sos': 'https://rutinasimple.com//cart/52919735222553:1',
  'jugos-7-dias|supervivencia-restaurantes|manual-sos': 'https://rutinasimple.com//cart/52919754260761:1'
};
type OrderBumpsProps = {
  onTotalChange?: (total: number) => void;
};
export function OrderBumps({
  onTotalChange
}: OrderBumpsProps) {
  const [selected, setSelected] = useState<string[]>([]);
  const [showExitOffer, setShowExitOffer] = useState(false);
  const selectedTotal = selected.reduce((sum, id) => {
    const bump = orderBumps.find(item => item.id === id);
    return sum + (bump?.price ?? 0);
  }, 0);
  const orderTotal = BASE_PRICE + selectedTotal;
  const selectionKey = BUMP_ORDER.filter(id => selected.includes(id)).join('|');
  const checkoutUrl = CHECKOUT_URLS[selectionKey] ?? BASE_CHECKOUT_URL;
  const handleCheckoutClick: React.MouseEventHandler<HTMLAnchorElement> = event => {
    if (selected.length === 0) {
      event.preventDefault();
      setShowExitOffer(true);
    }
  };
  const toggleBump = (id: string) => {
    setSelected(current => {
      const nextSelected = current.includes(id) ? current.filter(item => item !== id) : [...current, id];
      const nextSelectedTotal = nextSelected.reduce((sum, selectedId) => {
        const bump = orderBumps.find(item => item.id === selectedId);
        return sum + (bump?.price ?? 0);
      }, 0);
      onTotalChange?.(BASE_PRICE + nextSelectedTotal);
      return nextSelected;
    });
  };
  return <div id="completa-plan" className="mt-14 scroll-mt-24 border-t border-white/10 pt-12" data-id="element-299" data-mp="3h9pd8qh8x4s4">
      <Reveal data-id="element-300" data-mp="3cach1f4hilcv">
        <div className="mx-auto max-w-3xl text-center" data-id="element-301" data-mp="3c9t8gp0q59bi">
          <span className="inline-flex items-center gap-2 rounded-full bg-[#a9492f]/20 px-4 py-1.5 text-xs font-extrabold uppercase tracking-[0.16em] text-[#ffc09a]" data-id="element-302" data-mp="3c803rf9o8z65">
            <SparkIcon className="h-4 w-4" strokeWidth={2.2} data-id="element-303" data-mp="32b3s50oh7wxd" />
            Espera: tu método está incompleto sin esto
          </span>
          <h3 className="mt-4 font-display text-3xl font-black leading-tight text-white sm:text-4xl" data-id="element-304" data-mp="2yc4orkie8881">
            Los aceleradores que te protegen cuando la vida real se interpone
          </h3>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-white/70 sm:text-lg" data-id="element-305" data-mp="2oidyqb6mbpq8">
            El 90% de las mujeres que sanan más rápido activaron estos aceleradores. Solo aparecen aquí y jamás a este precio.
          </p>
        </div>
      </Reveal>

      <div className="mt-10 grid gap-6 lg:grid-cols-3" data-id="element-306" data-mp="2am0n3iormijo">
        {orderBumps.map((bump, index) => {
        const isSelected = selected.includes(bump.id);
        const Icon = bump.icon;
        return <Reveal key={bump.id} delay={index * 0.08} data-id="element-307" data-mp="250018afzw3pn" data-mpi={index}>
              <motion.article animate={{
            borderColor: isSelected ? 'rgba(127, 191, 116, 1)' : 'rgba(255, 255, 255, 0.12)'
          }} className={`flex h-full flex-col overflow-hidden rounded-3xl border-2 bg-white text-[#29312c] shadow-xl ${isSelected ? 'ring-4 ring-[#9fbd91]/20' : ''}`} data-id="element-308" data-mp="1qzp9742y8j2w">
                <div className="relative aspect-square overflow-hidden bg-[#f7f4ec]" data-id="element-309" data-mp="29xjeb70yiag0">
                  <img src={bump.image} alt={bump.alt} width="1024" height="1024" loading="lazy" decoding="async" className="h-full w-full object-cover object-center" data-id="element-310" data-mp="2evl6qik9vm45" />
                  <span className="absolute left-4 top-4 flex h-10 w-10 items-center justify-center rounded-xl bg-[#1f2a22]/90 text-[#9bd492] shadow-lg backdrop-blur" data-id="element-311" data-mp="3gpwbx8lm0swm">
                    <Icon className="h-6 w-6" strokeWidth={2} data-id="element-312" data-mp="3qghbay93y1gl" />
                  </span>
                </div>

                <div className="flex flex-1 flex-col p-5 sm:p-6" data-id="element-313" data-mp="3lj8xtaywmllo">
                  <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#3f6b3a]" data-id="element-314" data-mp="2xsd3a632dcaq">
                    {bump.eyebrow}
                  </p>
                  <h4 className="mt-2 font-display text-lg font-extrabold leading-snug text-[#1f2a22]" data-id="element-315" data-mp="2ttmagb5msgtf">
                    {bump.title}
                  </h4>
                  <p className="mt-3 text-sm leading-relaxed text-[#5a6b5c]" data-id="element-316" data-mp="32rg7job7zehn">
                    {bump.description}
                  </p>

                  <ul className="mt-5 space-y-3" data-id="element-317" data-mp="n2wojhzh3w2p">
                    {bump.benefits.map(benefit => <li key={benefit} className="flex items-start gap-2.5 text-sm text-[#3d4a40]" data-id="element-318" data-mp="i4d3djspeobe">
                        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#3f6b3a]/10 text-[#3f6b3a]" data-id="element-319" data-mp="2gjslytjeqezd">
                          <CheckIcon className="h-3 w-3" strokeWidth={3} data-id="element-320" data-mp="2lhucu0n2rile" />
                        </span>
                        <span data-id="element-321" data-mp="2qadrl0xi0jrs">{benefit}</span>
                      </li>)}
                  </ul>

                  <div className="mt-auto pt-6" data-id="element-322" data-mp="2v8fc7qakor5d">
                    <div className="flex items-end justify-between gap-3 border-t border-[#1f2a22]/10 pt-5" data-id="element-323" data-mp="307a37fip1m0k">
                      <div data-id="element-324" data-mp="34955oncd25fx">
                        <p className="text-xs font-medium uppercase tracking-wide text-[#7a877c]" data-id="element-325" data-mp="349wmut57o0mo">
                          Precio exclusivo
                        </p>
                        <p className="mt-1 font-display text-3xl font-black text-[#e0611f]" data-id="element-326" data-mp="19w5jgbdh6czo">
                          ${bump.price.toFixed(2)}
                        </p>
                      </div>
                      <p className="pb-1 text-sm text-[#7a877c]" data-id="element-327" data-mp="17b7h6l3vqap5">
                        Valor{' '}
                        <span className="font-semibold line-through" data-id="element-328" data-mp="12jxxs28xc3lp">
                          ${bump.originalPrice.toFixed(2)}
                        </span>
                      </p>
                    </div>

                    <button type="button" role="checkbox" aria-checked={isSelected} onClick={() => toggleBump(bump.id)} className={`mt-5 flex w-full items-center justify-center gap-3 rounded-xl border-2 px-4 py-3.5 font-display text-sm font-extrabold uppercase tracking-wide transition-colors focus:outline-none focus:ring-4 focus:ring-[#7fbf74]/30 ${isSelected ? 'border-[#3f6b3a] bg-[#3f6b3a] text-white' : 'border-[#3f6b3a]/25 bg-[#eef2e6] text-[#2f572e] hover:border-[#3f6b3a]'}`} data-id="element-329" data-mp="sncov5tsa8cl">
                      <span className={`flex h-5 w-5 items-center justify-center rounded border-2 ${isSelected ? 'border-white bg-white text-[#3f6b3a]' : 'border-[#3f6b3a]/50 bg-white text-transparent'}`} data-id="element-330" data-mp="20ttawfwzlmzu">
                        <CheckIcon className="h-3 w-3" strokeWidth={3} data-id="element-331" data-mp="2kghhhpu71kdm" />
                      </span>
                      {isSelected ? 'Acelerador seleccionado' : `Sí, agregar por ${bump.price.toFixed(2)}`}
                    </button>
                  </div>
                </div>
              </motion.article>
            </Reveal>;
      })}
      </div>

      <Reveal delay={0.12} data-id="element-332" data-mp="2oh1fevmlcs0t">
        <div className="mx-auto mt-10 max-w-2xl rounded-3xl border border-[#7fbf74]/30 bg-[#26332a] p-5 shadow-2xl sm:p-7" data-id="element-333" data-mp="z3edoxr3tujz">
          <div className="flex flex-col gap-4 border-b border-white/10 pb-5 sm:flex-row sm:items-end sm:justify-between" data-id="element-334" data-mp="u64qlor6tuzb">
            <div data-id="element-335" data-mp="35syg543b62h7">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#9bd492]" data-id="element-336" data-mp="35uj3a8namj51">
                Resumen de tu pedido
              </p>
              <p className="mt-2 text-sm text-white/65" data-id="element-337" data-mp="2wytncf4m6gz1">
                GastroSmart ${BASE_PRICE.toFixed(2)}
                {selected.length > 0 ? ` + ${selected.length} ${selected.length === 1 ? 'complemento' : 'complementos'}` : ' · Sin complementos seleccionados'}
              </p>
            </div>
            <div className="text-left sm:text-right" data-id="element-338" data-mp="2ryp4wjk5bc66">
              <p className="text-xs font-medium uppercase tracking-wide text-white/50" data-id="element-339" data-mp="2e22425p2o1uz">
                Total de hoy
              </p>
              <motion.p key={orderTotal} initial={{
              scale: 0.9,
              opacity: 0.6
            }} animate={{
              scale: 1,
              opacity: 1
            }} className="mt-1 font-display text-4xl font-black text-[#9bd492]" aria-live="polite" data-id="element-340" data-mp="2i4oovmnbsmio">
                ${orderTotal.toFixed(2)}
              </motion.p>
            </div>
          </div>

          <div id="checkout-final" className="mt-5 scroll-mt-24" data-id="element-341" data-mp="1k7rswo9o5ni7">
            <CtaButton checkout checkoutHref={checkoutUrl} trackingValue={orderTotal} onClick={handleCheckoutClick} label="CONTINUAR A MI PEDIDO" price={'TOTAL ' + String.fromCharCode(36) + orderTotal.toFixed(2)} data-id="element-342" data-mp="1g8ixleyzdhkj" />
          </div>
          <p className="mt-3 text-center text-xs leading-relaxed text-white/50" data-id="element-343" data-mp="23xvhp9vuja1m">
            Pago único y acceso inmediato. Puedes cambiar tu selección antes de continuar.
          </p>
        </div>
      </Reveal>

      <ExitIntentUpsell open={showExitOffer} onClose={() => setShowExitOffer(false)} data-id="element-344" data-mp="1u567sjfy248e" />
    </div>;
}